import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useDebouncedCallback } from 'use-debounce';
import type { UserQueryParams, UserStatus } from '../types/user.types';
import type { UserRole } from '../../../types/auth';

interface UserListFiltersProps {
  filters: UserQueryParams;
  onFiltersChange: (filters: UserQueryParams) => void;
}

export function UserListFilters({ filters, onFiltersChange }: UserListFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');

  const debouncedSearch = useDebouncedCallback((value: string) => {
    onFiltersChange({ ...filters, search: value || undefined, page: 1 });
  }, 300);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleRoleChange = useCallback(
    (event: any) => {
      const role = event.target.value as UserRole | '';
      onFiltersChange({
        ...filters,
        role: role || undefined,
        page: 1,
      });
    },
    [filters, onFiltersChange]
  );

  const handleStatusChange = useCallback(
    (event: any) => {
      const status = event.target.value as UserStatus | '';
      onFiltersChange({
        ...filters,
        status: status || undefined,
        page: 1,
      });
    },
    [filters, onFiltersChange]
  );

  const handleClearFilters = () => {
    setSearchValue('');
    onFiltersChange({});
  };

  const hasFilters = filters.search || filters.role || filters.status;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
      }}
    >
      <TextField
        placeholder="Search users..."
        value={searchValue}
        onChange={handleSearchChange}
        size="small"
        sx={{ minWidth: 250 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Role</InputLabel>
        <Select
          value={filters.role || ''}
          onChange={handleRoleChange}
          label="Role"
        >
          <MenuItem value="">All Roles</MenuItem>
          <MenuItem value="operator">Operator</MenuItem>
          <MenuItem value="supervisor">Supervisor</MenuItem>
          <MenuItem value="researcher">Researcher</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={filters.status || ''}
          onChange={handleStatusChange}
          label="Status"
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
        </Select>
      </FormControl>

      {hasFilters && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
      )}
    </Box>
  );
}
